/**
 * relaxometry.js — карты T2 и T1 по многоэховым и инверсионным сериям МРТ.
 *
 * ЗАЧЕМ. Обычная МРТ показывает «взвешенное» изображение: яркость зависит и от
 * ткани, и от настроек последовательности, поэтому сравнить два исследования
 * числом нельзя (ровно та беда, из-за которой понадобилась нормировка в
 * mrNormalize.js). Картирование даёт КОЛИЧЕСТВЕННУЮ величину в миллисекундах:
 * T2 и T1 — физические времена релаксации ткани. По ним отслеживают отёк,
 * фиброз, перегрузку железом, а главное — сравнивают динамику.
 *
 * МОДЕЛИ, и они здесь ровно две.
 *
 *   T2 (многоэховая серия, разные TE):
 *       S(TE) = S0 · exp(−TE / T2)
 *   Логарифмируем — получаем прямую: ln S = ln S0 − TE/T2. Наклон даёт T2.
 *   Подгонка ВЗВЕШЕННАЯ: вес = S². Логарифм искажает шум — тихие поздние эхо
 *   «весят» столько же, сколько громкие, хотя доверия к ним меньше, — и без веса
 *   оценка ЗАМЕТНО ТЕРЯЕТ ТОЧНОСТЬ. Проверено числом: шум 10% и хвост эхо до
 *   320 мс дают ошибку 0,04% со взвешиванием против 0,25% без него (в шесть раз
 *   хуже). Направление смещения при этом не фиксировано — оно зависит от модели
 *   шума, поэтому прежнее утверждение «уезжает вниз» из этого пояснения убрано:
 *   его проверка не подтвердила.
 *
 *   T1 (инверсия-восстановление, разные TI, данные по модулю):
 *       S(TI) = |S0 · (1 − 2·exp(−TI / T1))|
 *   Здесь прямой не получается: модуль ломает линейность. Считается перебором
 *   по сетке T1 (логарифмический шаг), для каждого T1 оптимальный S0 находится
 *   аналитически, берётся минимум суммы квадратов. Плюс отдельно возвращается
 *   грубая оценка по точке нуля: T1 ≈ TI_null / ln 2 — она нужна как проверка
 *   здравого смысла, а не как ответ.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Сказано заранее, чтобы никто не принял карту за клиническую:
 *
 *   · Нет коррекции B1 и несовершенства инверсии. Для T1 по IR это даёт
 *     систематическую ошибку; насколько — зависит от аппарата.
 *   · MOLLI и ShMOLLI (то, чем на практике мерят T1 миокарда) считаются ДРУГОЙ
 *     моделью: Look-Locker с возмущением от считывания, три параметра и поправка
 *     T1 = T1*·(B/A − 1). С 10.19.22 она есть — core/molliT1.js, — и серию с
 *     MOLLI в описании нужно считать ею.
 *
 *     [10.19.22] Здесь стояло «этой моделью MOLLI считать нельзя, T1 выйдет
 *     заниженным». ПРОВЕРКА ЭТОГО НЕ ПОДТВЕРДИЛА, и оставлять красивое
 *     утверждение нельзя. Замер на идеальной MOLLI-кривой (tests/molli_test.mjs):
 *     здешняя двухпараметрическая модель ошибается на 1–2 % при типичном
 *     отношении B/A = 2,1…2,4 — не занижает, а именно ошибается немного.
 *     Причина понятна: |S0·(1 − 2e^(−TI/T1))| — это та же модель с ЗАКРЕПЛЁННЫМ
 *     B/A = 2, и свободный S0 частично компенсирует расхождение. Ошибка
 *     становится заметной там, где отношение далеко от двух: при B/A = 3,2 и
 *     T1 = 1600 мс это уже 25 %. Настоящая беда не в величине ошибки, а в том,
 *     что двухпараметрическая модель НЕ ОТДАЁТ ни T1*, ни B/A — по её ответу
 *     нельзя понять, в каком из этих случаев вы находитесь.
 *   · Нет учёта многокомпонентной релаксации (жир + вода), стимулированных эхо
 *     в турбо-спин-эхо (там T2 систематически завышается), диффузии и потока.
 *   · Нет регистрации кадров между собой: если пациент двигался между эхо,
 *     карта будет смазана. Признак движения здесь не считается.
 *
 * Поэтому у каждой карты есть карта достоверности (R²) и правило отказа: воксель
 * без пригодных точек не получает «0 мс», он получает NaN, а число вокселей с
 * отказом возвращается вызывающему. Ноль в миллисекундах — это ложь, а NaN —
 * честное «не посчитано».
 */

const LN2 = Math.log(2);

/** Проверка входа, общая для обеих карт. */
function проверитьКадры(кадры, имяПараметра) {
  if (!Array.isArray(кадры) || кадры.length < 2) {
    return `нужно минимум два кадра с разным ${имяПараметра}`;
  }
  const длина = кадры[0]?.данные?.length;
  if (!длина) return 'в первом кадре нет данных';
  for (const к of кадры) {
    if (!к?.данные || к.данные.length !== длина) return 'кадры разного размера';
    const t = Number(к[имяПараметра]);
    if (!Number.isFinite(t) || t <= 0) return `у кадра нет положительного ${имяПараметра}`;
  }
  const набор = new Set(кадры.map((к) => Number(к[имяПараметра])));
  if (набор.size < 2) return `все кадры с одинаковым ${имяПараметра} — наклон не определён`;
  return null;
}

/**
 * Карта T2 по многоэховой серии.
 *
 * @param {Array<{te: number, данные: ArrayLike<number>}>} кадры
 * @param {{минСигнал?: number, минТочек?: number, maxT2?: number}} нстр
 * @returns {{надёжно: boolean, причина: string|null, t2: Float32Array, s0: Float32Array,
 *            r2: Float32Array, посчитано: number, отказов: number, эхо: number[]}}
 */
export function картаT2(кадры, нстр = {}) {
  const беда = проверитьКадры(кадры, 'te');
  const пусто = { t2: new Float32Array(0), s0: new Float32Array(0), r2: new Float32Array(0), посчитано: 0, отказов: 0, эхо: [] };
  if (беда) return { надёжно: false, причина: беда, ...пусто };

  const минСигнал = нстр.минСигнал ?? 0;
  // [10.19.14] Вес можно отключить — не «на всякий случай», а чтобы утверждение
  // «вес S² нужен» проверялось числом: контрольная мутация (убрать вес) иначе
  // проходила зелёной, потому что на чистых данных вес ничего не меняет.
  const безВеса = нстр.безВеса === true;
  const минТочек = Math.max(2, нстр.минТочек ?? 3);
  const maxT2 = нстр.maxT2 ?? 3000;
  if (кадры.length < минТочек) {
    return { надёжно: false, причина: `для устойчивой подгонки нужно ${минТочек} эхо и более, дано ${кадры.length}`, ...пусто };
  }

  const N = кадры[0].данные.length;
  const te = кадры.map((к) => Number(к.te));
  const t2 = new Float32Array(N).fill(NaN);
  const s0 = new Float32Array(N).fill(NaN);
  const r2 = new Float32Array(N).fill(NaN);
  let посчитано = 0, отказов = 0;

  for (let p = 0; p < N; p++) {
    // Взвешенный МНК по логарифму: вес = S².
    let сумВ = 0, сумВx = 0, сумВy = 0, сумВxx = 0, сумВxy = 0, точек = 0;
    for (let i = 0; i < кадры.length; i++) {
      const S = кадры[i].данные[p];
      if (!(S > 0) || S <= минСигнал) continue;
      const w = безВеса ? 1 : S * S;
      const x = te[i];
      const y = Math.log(S);
      сумВ += w; сумВx += w * x; сумВy += w * y; сумВxx += w * x * x; сумВxy += w * x * y;
      точек++;
    }
    if (точек < минТочек) { отказов++; continue; }

    const знаменатель = сумВ * сумВxx - сумВx * сумВx;
    if (Math.abs(знаменатель) < 1e-12) { отказов++; continue; }
    const наклон = (сумВ * сумВxy - сумВx * сумВy) / знаменатель;
    const пересечение = (сумВy - наклон * сумВx) / сумВ;

    // Сигнал обязан спадать: наклон < 0. Иначе это не релаксация (шум, артефакт,
    // движение) — и выдавать «T2 = 5000 мс» нельзя.
    if (!(наклон < 0)) { отказов++; continue; }
    const значение = -1 / наклон;
    if (!(значение > 0) || значение > maxT2) { отказов++; continue; }

    // R² по взвешенному логарифму — карта доверия к вокселю.
    const средY = сумВy / сумВ;
    let ssTot = 0, ssRes = 0;
    for (let i = 0; i < кадры.length; i++) {
      const S = кадры[i].данные[p];
      if (!(S > 0) || S <= минСигнал) continue;
      const w = S * S, y = Math.log(S);
      const пред = пересечение + наклон * te[i];
      ssTot += w * (y - средY) * (y - средY);
      ssRes += w * (y - пред) * (y - пред);
    }
    t2[p] = значение;
    s0[p] = Math.exp(пересечение);
    r2[p] = ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 1;
    посчитано++;
  }

  return {
    надёжно: посчитано > 0,
    причина: посчитано > 0 ? null : 'ни один воксель не дал спадающей кривой — это не многоэховая серия либо только шум',
    t2, s0, r2, посчитано, отказов, эхо: te,
  };
}

/**
 * Карта T1 по серии инверсия-восстановление (данные по модулю).
 *
 * @param {Array<{ti: number, данные: ArrayLike<number>}>} кадры
 * @param {{минT1?: number, максT1?: number, узлов?: number, минТочек?: number}} нстр
 */
export function картаT1(кадры, нстр = {}) {
  const беда = проверитьКадры(кадры, 'ti');
  const пусто = { t1: new Float32Array(0), s0: new Float32Array(0), r2: new Float32Array(0), посчитано: 0, отказов: 0, ti: [] };
  if (беда) return { надёжно: false, причина: беда, ...пусто };

  const минТочек = Math.max(3, нстр.минТочек ?? 3);
  if (кадры.length < минТочек) {
    return { надёжно: false, причина: `для T1 нужно ${минТочек} значения TI и более, дано ${кадры.length}`, ...пусто };
  }

  const минT1 = нстр.минT1 ?? 20;
  const максT1 = нстр.максT1 ?? 4000;
  const узлов = Math.max(32, нстр.узлов ?? 160);

  // Логарифмическая сетка: T1 тканей различается на порядок (жир ~250 мс,
  // жидкость ~3000 мс), и равномерная сетка тратила бы узлы не там, где нужно.
  const сетка = new Float64Array(узлов);
  const шагЛог = (Math.log(максT1) - Math.log(минT1)) / (узлов - 1);
  for (let i = 0; i < узлов; i++) сетка[i] = Math.exp(Math.log(минT1) + i * шагЛог);

  const N = кадры[0].данные.length;
  const ti = кадры.map((к) => Number(к.ti));
  const t1 = new Float32Array(N).fill(NaN);
  const s0 = new Float32Array(N).fill(NaN);
  const r2 = new Float32Array(N).fill(NaN);
  let посчитано = 0, отказов = 0;

  const модель = new Float64Array(кадры.length);

  for (let p = 0; p < N; p++) {
    let сумS = 0, точек = 0;
    for (let i = 0; i < кадры.length; i++) {
      const S = кадры[i].данные[p];
      if (!(S >= 0) || !Number.isFinite(S)) continue;
      сумS += S; точек++;
    }
    if (точек < минТочек || сумS <= 0) { отказов++; continue; }

    let лучшийT1 = NaN, лучшийS0 = NaN, лучшаяОшибка = Infinity;
    for (let k = 0; k < узлов; k++) {
      const T = сетка[k];
      // При фиксированном T1 модель линейна по S0: |1 − 2e^{−TI/T1}| — известный
      // множитель. Оптимальный S0 = Σ(S·f) / Σ(f²).
      let сумSf = 0, сумff = 0;
      for (let i = 0; i < кадры.length; i++) {
        const S = кадры[i].данные[p];
        if (!Number.isFinite(S)) continue;
        const f = Math.abs(1 - 2 * Math.exp(-ti[i] / T));
        модель[i] = f;
        сумSf += S * f; сумff += f * f;
      }
      if (!(сумff > 0)) continue;
      const S0 = сумSf / сумff;
      let ошибка = 0;
      for (let i = 0; i < кадры.length; i++) {
        const S = кадры[i].данные[p];
        if (!Number.isFinite(S)) continue;
        const d = S - S0 * модель[i];
        ошибка += d * d;
      }
      if (ошибка < лучшаяОшибка) { лучшаяОшибка = ошибка; лучшийT1 = T; лучшийS0 = S0; }
    }

    if (!(лучшийT1 > 0) || !(лучшийS0 > 0)) { отказов++; continue; }

    // R²: доля объяснённой изменчивости самого сигнала (не логарифма).
    const средS = сумS / точек;
    let ssTot = 0;
    for (let i = 0; i < кадры.length; i++) {
      const S = кадры[i].данные[p];
      if (!Number.isFinite(S)) continue;
      ssTot += (S - средS) * (S - средS);
    }
    t1[p] = лучшийT1;
    s0[p] = лучшийS0;
    r2[p] = ssTot > 0 ? Math.max(0, 1 - лучшаяОшибка / ssTot) : 1;
    посчитано++;
  }

  return {
    надёжно: посчитано > 0,
    причина: посчитано > 0 ? null : 'ни один воксель не подогнался под модель инверсии-восстановления',
    t1, s0, r2, посчитано, отказов, ti,
  };
}

/**
 * Грубая оценка T1 по точке нуля: T1 ≈ TI_null / ln 2.
 * Нужна как проверка порядка величины рядом с подгонкой, а не как ответ:
 * настоящий минимум лежит между кадрами, и разрешение здесь — шаг по TI.
 */
export function t1ПоТочкеНуля(кадры, индексВокселя) {
  if (!Array.isArray(кадры) || кадры.length < 2) return null;
  let лучший = null;
  for (const к of кадры) {
    const S = к?.данные?.[индексВокселя];
    const ti = Number(к?.ti);
    if (!Number.isFinite(S) || !Number.isFinite(ti)) continue;
    if (!лучший || S < лучший.S) лучший = { S, ti };
  }
  if (!лучший) return null;
  return { ti: лучший.ti, t1: лучший.ti / LN2 };
}

/**
 * Предупреждения по описанию серии — до всякого счёта.
 * Отдельная функция, чтобы интерфейс мог сказать это ДО того, как врач
 * посмотрит на цифры.
 */
export function предупрежденияПоСерии(описание = '') {
  const т = String(описание).toLowerCase();
  const п = [];
  if (/molli|shmolli/.test(т)) {
    // [10.19.22] Формулировка исправлена по замеру: слова «T1 выйдет заниженным»
    // проверка не подтвердила (см. шапку файла и tests/molli_test.mjs). Модель
    // MOLLI теперь есть, и предупреждение обязано вести к ней, а не пугать
    // числом, которого нет.
    п.push('серия MOLLI/ShMOLLI: считайте её трёхпараметрической моделью Look-Locker с поправкой (core/molliT1.js). Здешняя двухпараметрическая закрепляет B/A = 2: на типичных отношениях 2,1…2,4 ошибка около 1–2%, но при отношении далеко от двух — до 25%, и по её ответу нельзя понять, какой случай ваш');
  }
  if (/tse|turbo|fse|fast spin/.test(т)) {
    п.push('турбо-спин-эхо: из-за стимулированных эхо T2 систематически завышается');
  }
  if (/dixon|fat|жир/.test(т)) {
    п.push('в кадре может быть жир: двухкомпонентная релаксация одной экспонентой не описывается');
  }
  return п;
}

/** Средние значения по маске — для подписи «T1 миокарда N мс». */
export function среднееПоМаске(карта, маска, r2 = null, минR2 = 0) {
  if (!карта?.length) return { среднее: NaN, вокселей: 0, разброс: NaN };
  let сумма = 0, n = 0;
  const значения = [];
  for (let i = 0; i < карта.length; i++) {
    if (маска && !маска[i]) continue;
    const v = карта[i];
    if (!Number.isFinite(v)) continue;
    if (r2 && Number.isFinite(r2[i]) && r2[i] < минR2) continue;
    сумма += v; n++; значения.push(v);
  }
  if (!n) return { среднее: NaN, вокселей: 0, разброс: NaN };
  const среднее = сумма / n;
  let d = 0;
  for (const v of значения) d += (v - среднее) * (v - среднее);
  return { среднее, вокселей: n, разброс: Math.sqrt(d / n) };
}

export default { картаT2, картаT1, t1ПоТочкеНуля, предупрежденияПоСерии, среднееПоМаске };
